import { useEffect, useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Shield, Search, FileDown, Printer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import WarrantyClaims from "@/pages/WarrantyClaims";
import { PaginationBar, usePagination } from "@/components/Pagination";

const statusColor = {
  Purchased: "text-yellow-500 bg-yellow-500/10 border-yellow-500/40",
  Active: "text-green-500 bg-green-500/10 border-green-500/40",
  Expired: "text-red-500 bg-red-500/10 border-red-500/40",
  Claimed: "text-blue-400 bg-blue-500/10 border-blue-500/40",
};

const empty = { serial_number: "", item_id: "", customer_id: "", sale_date: "", duration_months: 12, status: "Active" };
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function WarrantyMaster() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("all");
  const [fComp, setFComp] = useState("all");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState({ ...empty, sale_date: todayISO() });

  const canManage = ["owner", "manager"].includes(user.role);

  const load = (query = q) => api.get(`/warranties${query ? `?q=${encodeURIComponent(query)}` : ""}`).then(r => setList(r.data));
  useEffect(() => {
    load("");
    api.get("/items?scope=all").then(r => setItems(r.data)).catch(() => {});
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))].sort();
  const companies = [...new Set(items.map(i => i.company).filter(Boolean))].sort();
  const itemCat = (itemId) => items.find(i => i.id === itemId)?.category || "";
  const itemComp = (itemId) => items.find(i => i.id === itemId)?.company || "";
  const filtered = list.filter(w =>
    (fCat === "all" || itemCat(w.item_id) === fCat) &&
    (fComp === "all" || itemComp(w.item_id) === fComp)
  );
  const pg = usePagination(filtered, q);

  const expiryPreview = (() => {
    if (!f.sale_date || !f.duration_months) return "";
    const d = new Date(f.sale_date);
    d.setMonth(d.getMonth() + Number(f.duration_months));
    return d.toISOString().slice(0, 10);
  })();

  const pickItem = (v) => {
    const it = items.find(x => x.id === v);
    setF(x => ({ ...x, item_id: v, duration_months: it?.warranty_months || x.duration_months }));
  };

  const save = async () => {
    try {
      const payload = { ...f, customer_id: f.customer_id || null, sale_date: f.sale_date || null, duration_months: Number(f.duration_months) };
      if (editId) await api.patch(`/warranties/${editId}`, payload);
      else await api.post("/warranties", payload);
      toast.success(editId ? "Warranty updated" : "Warranty record added");
      setF({ ...empty, sale_date: todayISO() }); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (w) => {
    setF({
      serial_number: w.serial_number, item_id: w.item_id, customer_id: w.customer_id || "",
      sale_date: w.sale_date || "", duration_months: w.duration_months,
      status: ["Purchased", "Claimed"].includes(w.status) ? w.status : "Active",
    });
    setEditId(w.id); setOpen(true);
  };

  const del = async (w) => {
    if (!confirm(`Delete warranty record ${w.serial_number}? This cannot be undone.`)) return;
    try { await api.delete(`/warranties/${w.id}`); toast.success("Warranty deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const fetchPdf = async (url, filename, download) => {
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement("a");
        a.href = objUrl; a.download = filename; a.click();
      } else window.open(objUrl, "_blank");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Shield className="w-3 h-3"/> Inventory · Warranties</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Warranty Master</h1>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF({ ...empty, sale_date: todayISO() }); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-warranty-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                <Plus className="w-4 h-4 mr-1"/> New Warranty
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-lg">
              <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Warranty</DialogTitle></DialogHeader>
              <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Serial Number *</Label>
                  <Input data-testid="w-serial" value={f.serial_number} onChange={(e) => setF(x => ({ ...x, serial_number: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border font-mono" placeholder="e.g. BAT-2026-001"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Item *</Label>
                  <Select value={f.item_id} onValueChange={pickItem}>
                    <SelectTrigger data-testid="w-item" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select item"/></SelectTrigger>
                    <SelectContent>{items.map(i => <SelectItem key={i.id} value={i.id}>{i.item_name}{i.item_code ? ` · ${i.item_code}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer</Label>
                  <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                    <SelectTrigger data-testid="w-customer" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Walk-in / Select customer"/></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date of Sale (blank = stays Purchased)</Label>
                    <Input data-testid="w-sale-date" type="date" value={f.sale_date} onChange={(e) => setF(x => ({ ...x, sale_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Warranty Duration (months) *</Label>
                    <Input data-testid="w-months" type="number" min="1" value={f.duration_months} onChange={(e) => setF(x => ({ ...x, duration_months: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                  </div>
                </div>
                {expiryPreview && (
                  <div className="text-xs text-muted-foreground font-mono border border-border rounded-sm p-2">
                    Warranty Expiry (auto): <span className="text-primary">{expiryPreview}</span>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
                  <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                    <SelectTrigger data-testid="w-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Purchased">Purchased</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Claimed">Claimed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="save-warranty-btn" onClick={save} disabled={!f.serial_number || !f.item_id} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                  {editId ? "Update" : "Save"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="records">
        <TabsList className="bg-secondary rounded-sm mb-4">
          <TabsTrigger data-testid="tab-warranty-records" value="records" className="text-xs uppercase tracking-wider">Warranty Records</TabsTrigger>
          <TabsTrigger data-testid="tab-warranty-claims" value="claims" className="text-xs uppercase tracking-wider">Warranty Claims</TabsTrigger>
        </TabsList>
        <TabsContent value="records">
      <div className="flex flex-wrap items-end gap-3 mb-4 border border-border bg-card rounded-sm p-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Search (serial, item, item code, customer)</Label>
          <div className="relative mt-1">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input data-testid="w-search" value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} placeholder="Type to search…" className="pl-7 rounded-sm bg-secondary border-border h-9"/>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Category</Label>
          <Select value={fCat} onValueChange={setFCat}>
            <SelectTrigger data-testid="w-filter-category" className="mt-1 rounded-sm bg-secondary border-border w-44 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Company</Label>
          <Select value={fComp} onValueChange={setFComp}>
            <SelectTrigger data-testid="w-filter-company" className="mt-1 rounded-sm bg-secondary border-border w-44 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="w-export-pdf" size="sm" variant="outline" onClick={() => fetchPdf(`${API}/warranties/export.pdf${q ? `?q=${encodeURIComponent(q)}` : ""}`, "warranties_export.pdf", true)} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> PDF</Button>
          <Button data-testid="w-print" size="sm" variant="outline" onClick={() => fetchPdf(`${API}/warranties/export.pdf${q ? `?q=${encodeURIComponent(q)}` : ""}`, "", false)} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Company</th>
              <th className="text-left p-3">Serial Number</th>
              <th className="text-left p-3">Item Name</th>
              <th className="text-left p-3">Item Code</th>
              <th className="text-left p-3">Purchase Inv #</th>
              <th className="text-left p-3">Purchase Date</th>
              <th className="text-right p-3">Duration</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Sale Inv #</th>
              <th className="text-left p-3">Sale Date</th>
              <th className="text-left p-3">Start Date</th>
              <th className="text-left p-3">Expiry Date</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={14} className="p-6 text-center text-muted-foreground">No warranty records found</td></tr>}
            {pg.pageItems.map(w => (
              <tr key={w.id} data-testid={`warranty-row-${w.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 text-xs">{itemComp(w.item_id) || "-"}</td>
                <td className="p-3 font-mono text-xs text-primary">{w.serial_number}</td>
                <td className="p-3 text-xs">{w.item_name}</td>
                <td className="p-3 font-mono text-[10px] text-muted-foreground">{w.item_code || "-"}</td>
                <td className="p-3 font-mono text-[10px] text-muted-foreground">{w.purchase_txn_number || (w.txn_number && w.computed_status === "Purchased" ? w.txn_number : "-")}</td>
                <td className="p-3 font-mono text-xs">{w.purchase_date || "-"}</td>
                <td className="p-3 text-right font-mono text-xs">{w.duration_months} mo</td>
                <td className="p-3 text-xs">{w.customer_name}</td>
                <td className="p-3 font-mono text-[10px] text-muted-foreground">{w.sale_txn_number || "-"}</td>
                <td className="p-3 font-mono text-xs">{w.sale_date || "-"}</td>
                <td className="p-3 font-mono text-xs">{w.start_date || "-"}</td>
                <td className="p-3 font-mono text-xs">{w.expiry_date || "-"}</td>
                <td className="p-3"><span data-testid={`warranty-status-${w.id}`} className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${statusColor[w.computed_status] || statusColor.Active}`}>{w.computed_status}</span></td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button data-testid={`w-card-pdf-${w.id}`} size="sm" variant="ghost" onClick={() => fetchPdf(`${API}/warranties/${w.id}/pdf`, `warranty_${w.serial_number}.pdf`, true)} className="h-7 w-7 p-0" title="Warranty card PDF"><FileDown className="w-3 h-3"/></Button>
                  <Button data-testid={`w-card-print-${w.id}`} size="sm" variant="ghost" onClick={() => fetchPdf(`${API}/warranties/${w.id}/pdf`, "", false)} className="h-7 w-7 p-0" title="Print warranty card"><Printer className="w-3 h-3"/></Button>
                  {canManage && <Button data-testid={`edit-warranty-${w.id}`} size="sm" variant="ghost" onClick={() => edit(w)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>}
                  {canManage && <Button data-testid={`del-warranty-${w.id}`} size="sm" variant="ghost" onClick={() => del(w)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
        </TabsContent>
        <TabsContent value="claims">
          <WarrantyClaims embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
